import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { TooltipStep, OnboardingTour } from '@/components/ui/tooltip-onboarding';

interface OnboardingContextType {
  // Tour management
  currentTour: OnboardingTour | null;
  availableTours: OnboardingTour[];
  isOnboardingActive: boolean;

  // Step management
  currentStep: TooltipStep | null;
  currentStepIndex: number;
  totalSteps: number;

  // Tour control
  startTour: (tourId: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  dismissOnboarding: () => void;

  // Progress tracking
  userProgress: Record<number, UserOnboardingProgress>;
  markStepCompleted: (stepId: number) => void;

  // Settings
  onboardingEnabled: boolean;
  setOnboardingEnabled: (enabled: boolean) => void;
}

interface UserOnboardingProgress {
  id: number;
  tourId: number;
  stepId?: number;
  status: 'started' | 'completed' | 'skipped' | 'dismissed';
  completedAt?: string;
  lastActiveStep?: number;
}

interface OnboardingProviderProps {
  children: ReactNode;
  userId?: string;
  userRoles?: string[];
  currentPage?: string;
  autoStart?: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ 
  children, 
  userId, 
  userRoles = [], 
  currentPage: initialPage = '/',
  autoStart = true 
}: OnboardingProviderProps) {
  const queryClient = useQueryClient();
  const [currentPage, setLocation] = useLocation();

  // State
  const [currentTour, setCurrentTour] = useState<OnboardingTour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [onboardingEnabled, setOnboardingEnabled] = useState(true);

  // Fetch available tours
  const { data: availableTours = [] } = useQuery<any[]>({
    queryKey: ['/api/onboarding/tours'],
    enabled: !!userId && onboardingEnabled,
  });

  // Fetch user progress
  const { data: userProgress = {} } = useQuery<any[]>({
    queryKey: ['/api/onboarding/progress', userId],
    enabled: !!userId && onboardingEnabled,
  });

  // Auto-start eligible tours - use ref to prevent infinite loops
  const startingTour = useRef(false);

  // Track auto-start attempts per session to prevent repeated launches
  const autoStartAttempted = useRef(false);

  // Mutations
  const startTourMutation = useMutation({
    mutationFn: async (tourId: number) => {
      return await apiRequest('POST', '/api/onboarding/start-tour', { tourId });
    },
    onSuccess: (data) => {
      setCurrentTour(data.tour);
      setCurrentStepIndex(data.lastActiveStep || 0);
      setIsOnboardingActive(true);
      startingTour.current = false; // Reset the starting flag
      autoStartAttempted.current = true; // Mark auto-start as attempted
      queryClient.invalidateQueries({ queryKey: ['/api/onboarding/progress'] });
    },
    onError: (error) => {
      // Do NOT use fallback mechanism - respecting user's previous progress is more important
      // than showing the tour when API fails. If they've already skipped/completed this tour,
      // we should never show it again regardless of API status.

      startingTour.current = false; // Reset the starting flag even on error
      autoStartAttempted.current = true; // Mark auto-start as attempted even on error
    },
  });

  const updateProgressMutation = useMutation({
    mutationFn: async ({ tourId, stepId, status, metadata }: {
      tourId: number | string;
      stepId?: number | string;
      status: string;
      metadata?: Record<string, any>;
    }) => {
      return await apiRequest('POST', '/api/onboarding/progress', { tourId, stepId, status, metadata });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/onboarding/progress'] });
    },
  });

  // Tour control functions
  const startTour = (tourId: number) => {
    const tour = availableTours.find((t: OnboardingTour) => t.id === tourId);
    if (tour) {
      // Allow restarting any tour - the backend will handle the logic
      startTourMutation.mutate(tourId);
    }
  };

  // Keep track of whether we've already checked auto-start for this user session
  const autoStartChecked = useRef(false);

  useEffect(() => {
    // Auto-start logic with session tracking
    if (!userId || !autoStart || !onboardingEnabled || isOnboardingActive || startingTour.current) {
      return;
    }

    // Prevent duplicate auto-start checks in the same session
    if (autoStartChecked.current) {
      return;
    }

    // Wait for tours and user progress to load
    if (!availableTours || availableTours.length === 0 || !userProgress) {
      return;
    }

    // Find tours that should auto-start for this user's role
    const eligibleTours = availableTours.filter((tour: OnboardingTour) => {
      if (!tour.autoStart) return false;

      // Check if tour is role-specific
      if (tour.targetRoles && tour.targetRoles.length > 0) {
        return userRoles.some(role => tour.targetRoles?.includes(role));
      }

      return true;
    });

    if (eligibleTours.length === 0) {
      autoStartChecked.current = true;
      return;
    }

    // Check if any eligible tour hasn't been started/interacted with yet
    for (const tour of eligibleTours) {
      const tourProgress = (userProgress as Record<string, any>)[tour.id];

      // Only auto-start if user has NEVER interacted with this tour
      if (!tourProgress) {
        // Double-check we haven't already attempted auto-start in this session
        if (autoStartAttempted.current) {
          return;
        }

        autoStartChecked.current = true;
        startTour(tour.id);
        return;
      }
    }

    autoStartChecked.current = true;
  }, [userId, autoStart, onboardingEnabled, isOnboardingActive, availableTours, userProgress, userRoles]);

  // Track the previous userId to only reset when user actually switches (not just object re-initialization)
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only reset if the userId actually changed (user switching), not if the user object was re-initialized
    if (userId && previousUserIdRef.current && previousUserIdRef.current !== userId) {
      autoStartChecked.current = false;
      autoStartAttempted.current = false;
    }

    // Update the previous userId reference
    previousUserIdRef.current = userId ?? null;
  }, [userId]);

  const nextStep = () => {
    if (!currentTour) return;

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < currentTour.steps.length) {
      // Mark current step as completed first
      if (currentTour.steps[currentStepIndex]) {
        updateProgressMutation.mutate({
          tourId: currentTour.id,
          stepId: currentTour.steps[currentStepIndex].id,
          status: 'completed',
          metadata: { completedAt: new Date().toISOString() }
        });
      }

      // Check if the next step requires navigation to a different page
      const nextStepData = currentTour.steps[nextIndex];
      const stepPage = nextStepData.page;
      
      // Normalize current page path (remove trailing slashes, handle root)
      const normalizedCurrentPage = currentPage === '/' ? '/' : currentPage.replace(/\/$/, '');
      const normalizedStepPage = stepPage === '/' || stepPage === '*' || !stepPage ? '/' : stepPage.replace(/\/$/, '');
      
      // Check if we need to navigate (step has a specific page that's different from current)
      const needsNavigation = stepPage && 
                             stepPage !== '*' && 
                             normalizedStepPage !== normalizedCurrentPage;
      
      if (needsNavigation) {
        setLocation(normalizedStepPage);

        // Set the step index after navigation with longer delay for page to render
        setTimeout(() => {
          setCurrentStepIndex(nextIndex);
        }, 800);
      } else {
        setCurrentStepIndex(nextIndex);
      }
    } else {
      completeTour();
    }
  };

  const previousStep = () => {
    if (!currentTour) return;

    const prevIndex = Math.max(0, currentStepIndex - 1);
    
    // Check if the previous step requires navigation to a different page
    const prevStepData = currentTour.steps[prevIndex];
    const stepPage = prevStepData.page;
    
    // Normalize current page path
    const normalizedCurrentPage = currentPage === '/' ? '/' : currentPage.replace(/\/$/, '');
    const normalizedStepPage = stepPage === '/' || stepPage === '*' || !stepPage ? '/' : stepPage.replace(/\/$/, '');
    
    // Check if we need to navigate
    const needsNavigation = stepPage && 
                           stepPage !== '*' && 
                           normalizedStepPage !== normalizedCurrentPage;
    
    if (needsNavigation) {
      setLocation(normalizedStepPage);
      setTimeout(() => {
        setCurrentStepIndex(prevIndex);
      }, 800);
    } else {
      setCurrentStepIndex(prevIndex);
    }
  };

  const skipTour = () => {
    if (!currentTour) return;

    updateProgressMutation.mutate({
      tourId: currentTour.id,
      status: 'skipped',
      metadata: { 
        skippedAt: new Date().toISOString(),
        preventAutoStart: true // Flag to prevent future auto-starts
      }
    });

    setIsOnboardingActive(false);
    setCurrentTour(null);
    setCurrentStepIndex(0);
  };

  const completeTour = () => {
    if (!currentTour) return;

    updateProgressMutation.mutate({
      tourId: currentTour.id,
      status: 'completed',
      metadata: { completedAt: new Date().toISOString() }
    });

    setIsOnboardingActive(false);
    setCurrentTour(null);
    setCurrentStepIndex(0);
  };

  const dismissOnboarding = () => {
    if (!currentTour) return;

    updateProgressMutation.mutate({
      tourId: currentTour.id,
      status: 'dismissed',
      metadata: { 
        dismissedAt: new Date().toISOString(),
        preventAutoStart: true // Flag to prevent future auto-starts
      }
    });

    setIsOnboardingActive(false);
    setCurrentTour(null);
    setCurrentStepIndex(0);
  };

  const markStepCompleted = (stepId: number) => {
    if (!currentTour) return;

    updateProgressMutation.mutate({
      tourId: currentTour.id,
      stepId,
      status: 'completed',
      metadata: { completedAt: new Date().toISOString() }
    });
  };

  // Get current step
  const currentStep = currentTour?.steps[currentStepIndex] || null;
  const totalSteps = currentTour?.steps.length || 0;

  // Filter steps by current page if applicable
  const pageFilteredSteps = currentTour?.steps.filter(step => 
    !step.page || step.page === currentPage || step.page === '*'
  ) || [];

  // Context value updates (debug logging removed for cleaner console)

  const value: OnboardingContextType = {
    currentTour,
    availableTours,
    isOnboardingActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    startTour,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    dismissOnboarding,
    userProgress,
    markStepCompleted,
    onboardingEnabled,
    setOnboardingEnabled,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    // Return safe defaults when context is not available (e.g., during HMR or outside provider)
    return {
      currentTour: null,
      availableTours: [],
      isOnboardingActive: false,
      currentStep: null,
      currentStepIndex: 0,
      totalSteps: 0,
      startTour: () => {},
      nextStep: () => {},
      previousStep: () => {},
      skipTour: () => {},
      completeTour: () => {},
      dismissOnboarding: () => {},
      userProgress: {},
      markStepCompleted: () => {},
      onboardingEnabled: false,
      setOnboardingEnabled: () => {},
    };
  }
  return context;
}

// Hook for specific component onboarding
export function useComponentOnboarding(componentName: string) {
  const {
    currentStep,
    isOnboardingActive,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    dismissOnboarding,
    markStepCompleted,
  } = useOnboarding();

  // Check if current step targets this component
  const isTargeted = currentStep?.target.includes(componentName) || 
                    currentStep?.target.includes(`[data-onboarding="${componentName}"]`);

  return {
    isActive: isOnboardingActive && isTargeted,
    currentStep: isTargeted ? currentStep : null,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    dismissOnboarding,
    markStepCompleted,
  };
}

// Helper hook for adding onboarding data attributes
export function useOnboardingTarget(targetName: string) {
  return {
    'data-onboarding': targetName,
    'data-testid': `onboarding-${targetName}`,
  };
}